import { useMemo } from "react"
import { Trans, useTranslation } from "react-i18next"
import { getIntlLocale } from "@/lib/i18n/format"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  TemperatureRanges,
  useTemperatureAlertSettings,
} from "@/hooks/useTemperatureAlertSettings"
import { useAppSelector } from "@/reducers/hooks"
import { selectLocation } from "@/reducers/locationsSelectors"
import { selectLocationJidFromChatJid } from "@/reducers/selectors"

const formatTemperature = (value: number) =>
  `${value.toLocaleString(getIntlLocale("en-GB"), { maximumFractionDigits: 1 })} °C`

const inRange = (reading: number | undefined, min: string, max: string) => {
  if (reading === undefined || (min === "" && max === "")) return undefined
  const low = min === "" ? -Infinity : parseFloat(min)
  const high = max === "" ? Infinity : parseFloat(max)
  return reading >= low && reading <= high
}

type SensorProps = {
  id: string
  sensor: string
  description: string
  reading: number | undefined
  min: keyof TemperatureRanges
  max: keyof TemperatureRanges
  ranges: TemperatureRanges
  onChange: (field: keyof TemperatureRanges, value: string) => void
  onCommit: () => void
  disabled: boolean
}

const SensorRange = ({
  id,
  sensor,
  description,
  reading,
  min,
  max,
  ranges,
  onChange,
  onCommit,
  disabled,
}: SensorProps) => {
  const { t } = useTranslation()
  const ok = inRange(reading, ranges[min], ranges[max])
  return (
    <div className="space-y-3 rounded-lg border border-border p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-foreground">{sensor}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <p
            data-testid={`${id}-reading`}
            className="text-sm text-muted-foreground"
          >
            {reading === undefined ? (
              t("chat.temperature.noReading")
            ) : (
              <Trans
                i18nKey="chat.temperature.now"
                values={{ temperature: formatTemperature(reading) }}
                components={{
                  b: (
                    <span className="font-semibold tabular-nums text-foreground" />
                  ),
                }}
              />
            )}
          </p>
          {ok !== undefined && (
            <Badge
              data-testid={`${id}-status`}
              variant={ok ? "positive" : "error"}
            >
              {ok
                ? t("chat.temperature.inRange")
                : t("chat.temperature.outOfRange")}
            </Badge>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {(
          [
            [min, t("chat.temperature.min")],
            [max, t("chat.temperature.max")],
          ] as const
        ).map(([field, label]) => (
          <div key={field} className="space-y-1.5">
            <Label htmlFor={`${id}-${field}`} className="text-xs">
              {label}
            </Label>
            <div className="relative">
              <Input
                id={`${id}-${field}`}
                type="number"
                step="0.1"
                inputMode="decimal"
                placeholder="—"
                value={ranges[field]}
                onChange={(e) => onChange(field, e.target.value)}
                onBlur={onCommit}
                disabled={disabled}
                className="pr-8"
              />
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">
                °C
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

type TemperatureAlertsProps = {
  ranges: TemperatureRanges
  readings: { t1: number | undefined; t2: number | undefined }
  onChange: (field: keyof TemperatureRanges, value: string) => void
  onCommit: () => void
  disabled: boolean
}

export const TemperatureAlerts = ({
  ranges,
  readings,
  onChange,
  onCommit,
  disabled,
}: TemperatureAlertsProps) => {
  const { t } = useTranslation()
  return (
    <div
      data-testid="temperature-tab"
      className="h-full space-y-3 overflow-y-auto bg-card p-4"
    >
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">
          {t("chat.temperature.title")}
        </p>
        <p className="text-xs text-muted-foreground">
          {t("chat.temperature.description")}
        </p>
      </div>
      <SensorRange
        id="temperature-t1"
        sensor="T1"
        description={t("chat.temperature.mainSensor")}
        reading={readings.t1}
        min="t1Min"
        max="t1Max"
        ranges={ranges}
        onChange={onChange}
        onCommit={onCommit}
        disabled={disabled}
      />
      <SensorRange
        id="temperature-t2"
        sensor="T2"
        description={t("chat.temperature.additionalSensor")}
        reading={readings.t2}
        min="t2Min"
        max="t2Max"
        ranges={ranges}
        onChange={onChange}
        onCommit={onCommit}
        disabled={disabled}
      />
    </div>
  )
}

const TemperatureWidgetBody = ({ driverJid }: { driverJid: string }) => {
  const { enabled, isLoading, ranges, change, save } =
    useTemperatureAlertSettings(driverJid)
  const locationJid = useAppSelector(
    useMemo(() => selectLocationJidFromChatJid(driverJid), [driverJid]),
  )
  const location = useAppSelector(
    useMemo(() => selectLocation(locationJid), [locationJid]),
  )

  if (!enabled) return null

  return (
    <TemperatureAlerts
      ranges={ranges}
      readings={{
        t1: location?.temperatureMain,
        t2: location?.temperatureAdditional,
      }}
      onChange={change}
      onCommit={save}
      disabled={isLoading}
    />
  )
}

export default TemperatureWidgetBody
